// datasource provider for ag-grid server-side row model
// docs: https://www.ag-grid.com/archive/25.3.0/vue-grid/server-side-model-datasource/


//import { DateTime } from 'luxon';


/* interface for params object passed from AG-Grid:
interface IServerSideGetRowsParams {
    // details for the request, simple object, can be converted to JSON
    request: IServerSideGetRowsRequest;  // see below

    // the parent row node. is the RootNode (level -1) if request is top level.
    // this is NOT part of the request as it cannot be serialised to JSON (a rowNode has methods)
    parentNode: RowNode;

    // success callback
    success(params: LoadSuccessParams): void;

    // fail callback, tell the grid the call failed so it can adjust its state
    fail(): void;

    // grid API
    api: GridApi;

    // column API
    columnApi: ColumnApi;
}


interface IServerSideGetRowsRequest {  // this is the actual JSON serializable params for what is needed
    // for Infinite Scroll (i.e. Partial Store) only, first row requested
   startRow: number;

   // for Infinite Scroll (i.e. Partial Store) only, last row requested
   endRow: number;

   // row group columns
   rowGroupCols: ColumnVO[];

   // value columns
   valueCols: ColumnVO[];

   // pivot columns
   pivotCols: ColumnVO[];

   // true if pivot mode is one, otherwise false
   pivotMode: boolean;

   // what groups the user is viewing
   groupKeys: string[];

   // if filtering, what the filter model is
   filterModel: any;

   // if sorting, what the sort model is
   sortModel: any;  // array of objects with keys 'colId' and 'sort' (asc/desc)
}

// we pass a VO (Value Object) of the column and not the column itself,
// so the data can be converted to a JSON string and passed to server-side
interface ColumnVO {
    id: string;
    displayName: string;
    field: string;
    aggFunc: string;
}



*/

import cloneDeep from 'lodash/cloneDeep'
import { BackendField } from "./colDefs";
import { 
    getFlowObjectApi,
    suppliosApi,
} from "@/js/api-service";
import { fromCurrentUserInfo } from '@/js/djangoContextHelpers';


// CLASSES

export default class GridProvider {  // implements the ag-grid server-side row model interface (getRows() method) + other things
    constructor({
        model_class,  // name of backend model class - such as 'QualityIssue'
        flowSubtype = null,  // 'subtype' of flowObject, IF USED (if we are working with a flow object)
        loadingRowsCallback = null,  // callback to call when loading starts/ends
        // pass in fixed filters/groups/etc.?  or pass those to parent component, and parent component passes them in here?
        filterModel = null,  // a preset/fixed filter model, object with keys being field names, getRows() cannot override filters for these cols 
        sortModel = null,  /// a preset/fixed sort model -- Array of { colId, sort } -- getRows() cannot override sorts for these cols
        includeFields = null,  // Array of field names to INCLUDE in the grid -- if null, include all fields
        excludeFields = null,  // Array of field names to EXCLUDE from the grid -- if null, exclude none
        fixedFilterKwargs = null,  // fixed filters for BACKEND to apply, NOT in filterModel structure, but in kwargs structure - might change this later
    }) {
        this.model_class = model_class;
        this.flowSubtype = flowSubtype || null;  // default to null instad of undefined or ''
        this.loadingRowsCallback = loadingRowsCallback;
        this.fixedFilterModel = filterModel ? { ...filterModel } : null;  // default to null, meaning 'no filtering' 
        this.fixedFilterKwargs = fixedFilterKwargs ? { ...fixedFilterKwargs } : null;  // backend applied fixed filters, not in filterModel structure
        this.fixedSortModel = sortModel ? [ ...sortModel ] : null;  // default to null, meaning 'no sorting'
        this.includeFields = Array.isArray(includeFields) ? [...includeFields] : [];
        this.excludeFields = Array.isArray(excludeFields) ? [...excludeFields] : [];
        // EXCEL DOWNLOAD STUFF
        this.isLoadingExcel = false;  // used in parent component
        this.loadExcelError = null;  // used in parent component
        this.allowExcelDownload = false;  // modify below if supported - used in parent component
        // create the API/axios instance
        // for some things (Company), we expect the same 2 endpoints on that viewset as on FlowObjectViewSet
        if (model_class === 'Company') {
            this.api = suppliosApi.vendorStandard;
            // TODO - set some settings for what features of the grid are / are not available for this config (such as excel download, etc.)
            this.allowExcelDownload = false;  // TODO - need to add endpoint on viewset
        } else if (model_class === 'CompanyLocation') {
            this.api = suppliosApi.companyLocation;  // manually added to this endpoint since it is not currenly a flow object
            this.allowExcelDownload = true;
        } else if (model_class === 'CompanySite') {
            this.api = suppliosApi.companySite;  // not a flow object -- same grid endpoints as CompanyLocation (use_vendor_sites tenants only)
            this.allowExcelDownload = true;
        } else if (model_class === 'Product') {
            this.api = suppliosApi.item;  // manually added to this endpoint since it is not currenly a flow object
            this.allowExcelDownload = true;
        } else if (model_class === 'CompanyProduct') {
            this.api = suppliosApi.companyProduct;  // manually added to this endpoint since it is not currenly a flow object
            this.allowExcelDownload = true;
        } else if (model_class === 'Contact') {
            // 'Contact' is not a model class, but its the contact version of the TU model that we want to work with in a specific version of the grid
            // if you pass this.model_class to other places later, you'll need to account for this not being a real model class!
            this.api = suppliosApi.contact;
            this.allowExcelDownload = fromCurrentUserInfo('is_tenant_admin', false);  // TODO - make this only available for tenantadmins!  (and need to match perms on backend)
        } else {
            this.api = getFlowObjectApi(model_class, flowSubtype);
            this.allowExcelDownload = true;
        }

        // instantiate things that we'll get from API 
        this.isLoaded = false;  // set true after we fetch -- should only happen once
        this.loadError = null;  // set to error object if we get an error while loading
        this.hasObjectUrl = true;  // set to false if we are not using permalinked model class or some other _detail_url, this would addLinkCellType to the first column if true
        this.backendFields = [];  // replace with BackendField objects that we get from API
        this.gridContext = {};  // add things in here from load() 
        this.gridPresets = null;  // maybe set default when you figure out how you want to handle these
        // store context data somewhere?  or keep in each colDef?
        
        // TRACK ROW LOADING STATE (status/counts)
        this.isLoadingMeta = false;
        this.isLoadingRows = false;  
        this.lastTopLevelRowCount = 0;
        this.lastTotalItemCount = 0;
        this.lastFilteredItemCount = 0;

        this.lastGridRequest = {};  // the SSRM Grid Request (interface IServerSideGetRowsRequest) - stash here each time you use it so you can re-use it to get the Excel download of same stuff

        console.log('end of GridProvider constructor');
    }
    // GETTERS - they don't work that well with vue so just use them locally

    // API METHODS
    async load() {
        // make API call to get all metadata on this model class -- mainly the available fields (and options/choices?)
        // need this to be done before making other calls
        // don't check if already isLoaded... so we can re-load if needed
        console.log('from GridDatasource.load() - will now fetch meta');
        this.isLoadingMeta = true;
        // HARD-CODED FOR FLOW OBJECTS FOR NOW
        let resp;
        try {
            resp = await this.api.provide_grid_meta({
                include: this.includeFields, 
                exclude: this.excludeFields, 
                fixedFilterKwargs: this.fixedFilterKwargs,
            });  
        } catch (error) {
            console.error(`error fetching meta for model_class ${this.model_class}: `, error);
            this.loadError = error;
        }
        if (resp?.data) {
            const { columns, grid_presets, grid_context, has_object_url } = resp.data;  // pull out columns and grid_presets, and put the rest in gridContext
            console.log('loaded fields from API: ', columns);
            this.hasObjectUrl = has_object_url ?? true;  // default to true if not set
            this.gridContext = Object.assign({}, this.gridContext, grid_context);  // set a new object so its reactive... but preserve existing context
            this.gridPresets = grid_presets ?? [];  // should always be an Array
            const fields = columns && Array.isArray(columns) ? columns.map(c => new BackendField(c)) : [];
            this.backendFields = fields;
            this.isLoaded = true;
        } else {
            this.loadError = new Error('no data returned from API');
        }
        this.isLoadingMeta = false;
    }
    getAgGridColDefs() {
        // must call .load() first!
        if (!this.isLoaded) {
            throw new Error('must call .load() first!');
        }
        if (this.loadError) {
            throw this.loadError; // throw error, let getRows() catch it and call params.fail()
        }
        const colDefs = this.backendFields.map(f => f.getAgGridColDef(this.gridContext));  // some cols get their options (refData) from the grid context
        // console.log('colDefs from backendFields before adding linkCell', colDefs);
        const addLinkCellType = (col) => {  // mutates the colDef to add 'linkCell' to the type list
            const colDefTypeList = col['type'] && Array.isArray(col['type']) ? [...col['type']] : [];
            colDefTypeList.push('linkCell');
            col['type'] = colDefTypeList;
        };
        const isGroupCol = (col) => {
            return col['children'] && Array.isArray(col['children']) && col['children'].length > 0;
        };
        return colDefs.map((cd, idx) => {
            // if its the first one in list, make it the linkCell - could be a group column, if so, make the first child the linkCell
            // we will also PIN it left if its the first column (we do that in linkCell type)
            // TODO: Use the 'hasObjectUrl' to determine if we should add linkCell type
            if (idx === 0 && this.hasObjectUrl) {
            // if (idx === 0) {
                if (isGroupCol(cd)) {
                    const firstChild = cd['children'][0];
                    addLinkCellType(firstChild);
                } else {
                    addLinkCellType(cd);
                }
            }
            return cd;
        });
    }
    getFinalSortModel(gridRequestParams) {
        // sortModel is array of { colId: 'the_col_id', sort: 'asc' or 'desc' }
        // if we have fixedSortModel, then we IGNORE any sortModel passed in params, just use the fixedSortModel (they should be same anyways, since grid will get passed the fixed one)
        if (this.fixedSortModel && this.fixedSortModel.length > 0) {
            return [...this.fixedSortModel];
        }
        // if no fixedSortModel, then we use grid sortModel, if set
        return gridRequestParams?.sortModel ? [...gridRequestParams.sortModel] : null;
    }
    getFinalFilterModel(gridRequestParams) {
        // filterModel is Object keyed by colId
        // if we have fixedFilterModel, then we must merge them (top-level merge only) -- fixedFilterModel should win
        const fixedFilters = this.fixedFilterModel ? {...this.fixedFilterModel} : {};
        const gridFilters = gridRequestParams?.filterModel ? {...gridRequestParams.filterModel} : {};
        return Object.assign({}, gridFilters, fixedFilters);
    }
    async fetchRows(gridRequestParams) {
        // actually make the call to backend to get data -- call this from within getRows() 
        if (this.loadingRowsCallback) {
            this.loadingRowsCallback(true);
        }
        this.isLoadingRows = true;
        if (!this.isLoaded && !this.loadError) {
            await this.load();
        }
        if (this.loadError) {
            throw this.loadError; // throw error, let getRows() catch it and call params.fail()
        }
        const gridRequest = cloneDeep(gridRequestParams);
        const overrides = {
            filterModel: this.getFinalFilterModel(gridRequestParams),
            sortModel: this.getFinalSortModel(gridRequestParams),
        };
        Object.assign(gridRequest, overrides);  // merge in the overrides
        // STASH IT SO WE KNOW WHAT WE JUST CALLED FOR, CAN GET EXCEL VERSION OF IT LATER
        this.lastGridRequest = gridRequest;
        // make the call to backend
        // const resp = await this.api.get_filter_list();  // TODO - change when you change to new endpoint
        // const rows = resp.data.items && Array.isArray(resp.data.items) ? resp.data.items : [];
        const resp = await this.api.provide_grid_data({
            gridRequest: gridRequest, 
            include: this.includeFields, 
            exclude: this.excludeFields, 
            fixedFilterKwargs: this.fixedFilterKwargs,
        });  // TODO - change when you change to new endpoint
        const rows = resp.data.items && Array.isArray(resp.data.items) ? resp.data.items : [];  // NOT ALL ITEMS, ONLY PARTIAL STORE REQUESTED BY ag-grid 
        const topLevelRowCount = resp.data.top_level_row_count ? resp.data.top_level_row_count : 0;
        const totalItemCount = resp.data.total_item_count ? resp.data.total_item_count : 0;
        const filteredItemCount = resp.data.filtered_item_count ? resp.data.filtered_item_count : 0;
        // SET COUNTS AS OF LAST LOAD
        this.lastTopLevelRowCount = topLevelRowCount;
        this.lastTotalItemCount = totalItemCount;
        this.lastFilteredItemCount = filteredItemCount;
        // let error bubble up if there is one -- getRows() will catch it and call params.fail()
        // TODO - change above when you change to new endpoint
        this.isLoadingRows = false;
        if (this.loadingRowsCallback) {
            this.loadingRowsCallback(false);
        }
        return {
            rows: rows,
            topLevelRowCount: topLevelRowCount,
            totalItemCount: totalItemCount,
            filteredItemCount: filteredItemCount,
        }
    }
    getRows(params) {  // params schema: ag-grid IServerSideGetRowsParams 
        // this is the method that AG-Grid calls to get rows (MUST BE CALLED 'getRows') -- I think it should not be async... should resolve the Promise in here?
        console.log('getRows params from ag-grid: ', params);  // will have { request, parentNode, success, fail, api, columnApi }
        console.log('server-side gridRequest from ag-grid: ', params.request);
        // fetch the rows, pass to params.success({rowData: rows, rowCount: lastRowInOverallCountIfUsingPartialStore, storeInfo: anyExtraInfoForGridToAssociateWithThisLoad })
        // or if fail, call params.fail()
        this.fetchRows(params.request).then((respData) => {
            console.log('got rows from backend: ', respData);
            params.success({
                rowData: respData.rows,
                rowCount: respData.topLevelRowCount,  // rowCount here is the number of rows for whatever top-level rows we have (first group rows, or all items if no grouping) - used to set scroll position of vertical scrollbar 
            });
        }).catch((error) => {
            console.error('error fetching rows: ', error);
            params.fail();
        });
    }

    // EXCEL DOWNLOAD
    async getExcelDownload({visibleFields} = {}) {
        // we always get the 'config' from the last gridRequest we stashed, so we get the Excel version of what's currently in the grid
        const finalVisibleFields = visibleFields && Array.isArray(visibleFields) ? [...visibleFields] : [];
        if (!this.allowExcelDownload) {
            console.error('Excel download not allowed');
            return;
        }
        this.isLoadingExcel = true;
        console.log('in getExcelDownload - server-side gridRequest: ', this.lastGridRequest, 'finalVisibleFields: ', finalVisibleFields);
        // that SHOULD have all the fixed stuff merged into it already
        try {
            await this.api.download_object_grid_excel({
                gridRequest: this.lastGridRequest, 
                include: this.includeFields, 
                exclude: this.excludeFields,
                visibleFields: finalVisibleFields,
                fixedFilterKwargs: this.fixedFilterKwargs,
            });
        } catch (error) {
            console.error(`error getting Excel download for model_class ${this.model_class}: `, error);
            this.loadExcelError = error;
        }
        this.isLoadingExcel = false;
    }

    // STATIC METHODS
    // static async fromApi({id, model_class}) {
    //     const flowObjectInst = new FlowObject({id, model_class, data: {}});  // instantiate with empty data, then fetch from API immediately
    //     await flowObjectInst.fetchAll();
    //     return flowObjectInst;
    // }
}
